<template>
  <step :active="activeStep"></step>
  <div class="eticket-page">
    <div class="page-header">
      <div class="header-title">请选择您需要购买的门票</div>
    </div>
    
    <div class="eticket-container">
      <van-radio-group v-model="tktChecked" @change="changeTktType">
        <div 
          class="tkt-card" 
          v-for="(item, index) in tktData" 
          :key="index"
          :class="{ 'tkt-card--active': tktChecked === item.eTicket_ID }"
          @click="tktChecked = item.eTicket_ID; changeTktType(item.eTicket_ID)"
        >
          <!-- 票类型标签 -->
          <div class="tkt-badge">
            <span class="badge-text">{{ item.eTicket_Name }}</span>
          </div>
          
          <!-- 选中标记 -->
          <div class="tkt-check" v-if="tktChecked === item.eTicket_ID">
            <van-icon name="success" :color="mainColor" size="20" />
          </div>
          <div class="tkt-check tkt-check--empty" v-else></div>
          
          <!-- 票内容区 -->
          <div class="tkt-content">
            <div class="tkt-info">
              <div class="tkt-remark" v-if="item.eticket_Remark">
                <van-icon name="info-o" size="12" />
                {{ item.eticket_Remark }}
              </div>
            </div>
            
            <div class="tkt-price-row">
              <div class="price-box">
                <span class="price-symbol">¥</span>
                <span class="price-num">{{ item.eTicket_Money }}</span>
                <span class="price-unit">/张</span>
              </div>
              
              <div class="quantity-box">
                <div class="stepper-con">
                  <button 
                    class="stepper-btn stepper-btn--minus" 
                    :disabled="item.num === '0'"
                    @click.stop="delNum(item)"
                  >
                    <van-icon name="minus" size="14" />
                  </button>
                  <span class="stepper-num">{{ item.num }}</span>
                  <button 
                    class="stepper-btn stepper-btn--plus" 
                    @click.stop="plusNum(item)"
                  >
                    <van-icon name="plus" size="14" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 底部总价 -->
          <div class="tkt-footer" v-if="item.num !== '0'">
            <div class="total-label">小计</div>
            <div class="total-price">
              <span class="total-symbol">¥</span>
              <span class="total-num">{{ computPrice(item) }}</span>
            </div>
          </div>
        </div>
      </van-radio-group>
      
      <!-- 空状态 -->
      <div v-if="tktData.length === 0" class="empty-state">
        <van-empty description="暂无可用票种" />
      </div>
    </div>
  </div>
  
  <!-- 底部支付栏 -->
  <div class="pay-bar">
    <div class="pay-info">
      <div class="pay-label">合计金额</div>
      <div class="pay-price">
        <span class="pay-symbol">¥</span>
        <span class="pay-num">{{ totalPrice }}</span>
      </div>
    </div>
    <van-button
      round
      class="pay-btn"
      :color="mainColor"
      type="primary"
      :loading="btnLoading"
      :disabled="!tktChecked || selectedTkt?.num === '0'"
      @click="confirmPay()"
    >
      确认支付
    </van-button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { showToast } from 'vant'
import api from '@/api'
import step from '@/components/step.vue'

const router = useRouter()
const store = useStore()
const ExhCode = computed(() => store.state.ExhCode)
const mainColor = computed(() => store.state.mainColor)
const token = computed(() => store.state.token)
const VisitorID = computed(() => store.state.VisitorID)
const openid = computed(() => store.state.openid)
const isWeX = computed(() => store.state.isWeX)
const gconfig = computed(() => store.state.gconfig)

const tktChecked = ref('')
const tktData = ref([])
const btnLoading = ref(false)

// 计算当前步骤
const activeStep = computed(() => {
  // 购买门票是当前激活步骤
  // 步骤索引：基本信息(0) -> 问卷调查(1,可选) -> 购买门票(2或1) -> 登记成功(3或2)
  let step = 1 // 基本信息完成，从1开始
  if (gconfig.value.IsHaveQuestion === 'True') {
    step++ // 问卷调查完成
  }
  // 当前是购买门票步骤
  return step
})

// 计算选中的票
const selectedTkt = computed(() => {
  return tktData.value.find(item => item.eTicket_ID === tktChecked.value)
})

// 计算总价
const totalPrice = computed(() => {
  if (!selectedTkt.value || selectedTkt.value.num === '0') {
    return '0.00'
  }
  return selectedTkt.value.totalPrice || '0.00'
})

onMounted(() => {
  getTktList()
})

// 获取票列表
const getTktList = async () => {
  try {
    const res = await api.getEtktApi({ ExhCode: ExhCode.value })
    if (res.code === 200 && res.data) {
      tktData.value = res.data.map((item) => ({
        eTicket_ID: item.eTicket_ID,
        eTicket_Name: item.eTicket_Name || item.eticket_Ename,
        eTicket_Money: item.eTicket_Money,
        eticket_Remark: item.eticket_Remark,
        eticket_MostOnce: item.eticket_MostOnce || 10,
        num: '0',
        totalPrice: 0
      }))
    }
  } catch (error) {
    showToast('获取票列表失败')
  }
}

// 计算价格
const computPrice = (obj) => {
  const price = (parseFloat(obj.eTicket_Money) * parseInt(obj.num || 0)).toFixed(2)
  obj.totalPrice = price
  return price
}

// 减少数量
const delNum = (obj) => {
  if (obj.eTicket_ID !== tktChecked.value) {
    showToast('请先勾选此票')
    return
  }
  let num = parseInt(obj.num)
  if (num > 0) {
    num--
  } else {
    num = 0
  }
  obj.num = num.toString()
}

// 增加数量
const plusNum = (obj) => {
  if (obj.eTicket_ID !== tktChecked.value) {
    showToast('请先勾选此票')
    return
  }
  let num = parseInt(obj.num)
  const maxNum = parseInt(obj.eticket_MostOnce)
  if (num < maxNum) {
    num++
  } else {
    num = maxNum
    showToast(`最多购买${maxNum}张`)
  }
  obj.num = num.toString()
}

// 切换票类型
const changeTktType = (data) => {
  tktData.value.forEach((item) => {
    if (item.eTicket_ID !== data) {
      item.num = '0'
    } else {
      // 默认选择1张
      if (item.num === '0') {
        item.num = '1'
      }
    }
  })
}

// 确认支付
const confirmPay = async () => {
  if (!tktChecked.value) {
    showToast('请先选择您要购买的票类型')
    return
  }

  const selectedTkt = tktData.value.find((item) => item.eTicket_ID === tktChecked.value)

  if (!selectedTkt || selectedTkt.num === '0') {
    showToast('至少购买一张票')
    return
  }

  btnLoading.value = true

  try {
    const tkts = [
      {
        id: selectedTkt.eTicket_ID,
        Num: selectedTkt.num
      }
    ]

    const res = await api.submitPayOrderApi({
      token: token.value,
      ExhCode: ExhCode.value,
      PayType: isWeX.value ? 'wechat' : 'H5',
      VisitorID: VisitorID.value,
      OpenID: openid.value,
      ReturnSuccessUrl: window.location.href.split('#')[0] + '#/success?payBack=true',
      ReturnFailUrl: window.location.href,
      ticketList: tkts
    })

    if (res.code === 200) {
      if (res.data.payurl) {
        // 跳转到支付页面
        window.location.href = res.data.payurl
      } else if (res.data.payInfo) {
        // 微信支付
        handleWechatPay(res.data.payInfo)
      } else {
        // 直接跳转到成功页
        router.push({ path: '/success' })
      }
    } else {
      showToast(res.message || '支付失败')
    }
  } catch (error) {
    showToast('支付请求失败')
  } finally {
    btnLoading.value = false
  }
}

// 微信支付处理
const handleWechatPay = (payInfo) => {
  if (typeof WeixinJSBridge !== 'undefined') {
    WeixinJSBridge.invoke(
      'getBrandWCPayRequest',
      {
        appId: payInfo.appId,
        timeStamp: payInfo.timeStamp,
        nonceStr: payInfo.nonceStr,
        package: payInfo.package,
        signType: payInfo.signType,
        paySign: payInfo.paySign
      },
      (res) => {
        if (res.err_msg === 'get_brand_wcpay_request:ok') {
          router.push({ path: '/success' })
        } else {
          showToast('支付取消或失败')
        }
      }
    )
  } else {
    showToast('微信支付初始化失败')
  }
}
</script>

<style lang="less" scoped>
// 页面背景
.eticket-page {
  background: linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%);
  padding-bottom: 100px;
}

// 页面头部
.page-header {
  text-align: center;
  padding: 24px 16px 16px;
  background: #fff;
  margin-bottom: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

  .header-title {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 6px;
  }

  .header-subtitle {
    font-size: 13px;
    color: #999;
  }
}

// 票列表容器
.eticket-container {
  padding: 0 16px;
}

// 票卡片
.tkt-card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  margin-bottom: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;

  &:active {
    transform: scale(0.99);
  }

  &--active {
    border-color: var(--color-primary, #E60044);
    box-shadow: 0 4px 24px rgba(230, 0, 68, 0.12);
  }
}

// 票类型标签
.tkt-badge {
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(135deg, var(--color-primary, #E60044) 0%, #ff4757 100%);
  color: #fff;
  padding: 6px 16px;
  border-radius: 16px 0 16px 0;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(230, 0, 68, 0.3);

  .badge-text {
    display: block;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// 选中标记
.tkt-check {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &--empty {
    border: 2px solid #e0e0e0;
    box-shadow: none;
  }
}

// 票内容区
.tkt-content {
  margin-top: 32px;
}

// 票信息
.tkt-info {
  margin-bottom: 16px;

  .tkt-name {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 8px;
  }

  .tkt-remark {
    font-size: 12px;
    color: #ff6b6b;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(255, 107, 107, 0.08);
    padding: 6px 10px;
    border-radius: 6px;
    width: fit-content;
  }
}

// 价格和数量行
.tkt-price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px dashed #f0f0f0;
}

// 价格
.price-box {
  display: flex;
  align-items: baseline;

  .price-symbol {
    font-size: 16px;
    color: #ff4757;
    font-weight: 600;
  }

  .price-num {
    font-size: 28px;
    color: #ff4757;
    font-weight: 700;
    margin: 0 2px;
  }

  .price-unit {
    font-size: 13px;
    color: #999;
  }
}

// 数量选择器
.stepper-con {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 4px;

  .stepper-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #fff;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:active {
      transform: scale(0.95);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &--minus {
      color: #666;
    }

    &--plus {
      color: var(--color-primary, #E60044);
    }
  }

  .stepper-num {
    min-width: 40px;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    padding: 0 8px;
  }
}

// 底部小计
.tkt-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f5f5f5;

  .total-label {
    font-size: 13px;
    color: #666;
  }

  .total-price {
    .total-symbol {
      font-size: 14px;
      color: #ff4757;
      font-weight: 600;
    }

    .total-num {
      font-size: 20px;
      color: #ff4757;
      font-weight: 700;
    }
  }
}

// 底部支付栏
.pay-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  padding: 12px 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  z-index: 100;

  .pay-info {
    flex: 1;

    .pay-label {
      font-size: 12px;
      color: #999;
      margin-bottom: 2px;
    }

    .pay-price {
      .pay-symbol {
        font-size: 14px;
        color: #ff4757;
        font-weight: 600;
      }

      .pay-num {
        font-size: 28px;
        color: #ff4757;
        font-weight: 700;
      }
    }
  }

  .pay-btn {
    min-width: 140px;
    height: 46px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 23px !important;
    box-shadow: 0 4px 16px rgba(230, 0, 68, 0.3);

    &:disabled {
      opacity: 0.5;
      box-shadow: none;
    }
  }
}

// 空状态
.empty-state {
  padding: 60px 0;
}

// PC端适配
@media screen and (min-width: 768px) {
  .eticket-page {
    padding-bottom: 120px;
  }

  .page-header {
    padding: 32px 40px 24px;

    .header-title {
      font-size: 24px;
    }

    .header-subtitle {
      font-size: 14px;
    }
  }

  .eticket-container {
    padding: 0 40px;
    max-width: 800px;
    margin: 0 auto;
  }

  .tkt-card {
    padding: 28px;
    margin-bottom: 20px;

    &:hover {
      box-shadow: 0 6px 28px rgba(0, 0, 0, 0.1);
    }
  }

  .tkt-badge {
    padding: 8px 20px;
    font-size: 14px;

    .badge-text {
      max-width: 150px;
    }
  }

  .tkt-content {
    margin-top: 36px;
  }

  .tkt-info {
    .tkt-name {
      font-size: 20px;
    }

    .tkt-remark {
      font-size: 13px;
      padding: 8px 12px;
    }
  }

  .price-box {
    .price-num {
      font-size: 32px;
    }
  }

  .stepper-con {
    .stepper-btn {
      width: 36px;
      height: 36px;
    }

    .stepper-num {
      min-width: 48px;
      font-size: 18px;
    }
  }

  .pay-bar {
    padding: 16px 40px 32px;

    .pay-btn {
      min-width: 180px;
      height: 52px;
      font-size: 18px;
      border-radius: 26px !important;
    }
  }
}
</style>
